// SWIM CDN — asset URL constants
// CDN: https://cdn.swimflows.com (Cloudflare R2 — swim-cdn bucket)
// All web images reference these constants. No hardcoded CDN URLs anywhere else.
// Favicons are the only exception — served from /public/ (same-origin required).

export const CDN = 'https://cdn.swimflows.com'

export const ASSETS = {
  logos: {
    // ── Horizontal (icon + "WIM" text) ─────────────────────────────────────
    // Use in: nav (hover animation), email headers, social previews
    horizontal: {
      black: {
        xs:  `${CDN}/logos/swim-horizontal-black-xs.svg`,
        sm:  `${CDN}/logos/swim-horizontal-black-sm.svg`,
        md:  `${CDN}/logos/swim-horizontal-black-md.svg`,
        lg:  `${CDN}/logos/swim-horizontal-black-lg.svg`,
        lgPng: `${CDN}/logos/swim-horizontal-black-lg.png`,
      },
      white: {
        xs:  `${CDN}/logos/swim-horizontal-white-xs.svg`,
        sm:  `${CDN}/logos/swim-horizontal-white-sm.svg`,
        md:  `${CDN}/logos/swim-horizontal-white-md.svg`,
        lg:  `${CDN}/logos/swim-horizontal-white-lg.svg`,
        lgPng: `${CDN}/logos/swim-horizontal-white-lg.png`,
      },
    },

    // ── Icon only (swimmer icon — no wordmark) ──────────────────────────────
    // Use in: LogoMenu at-rest state, favicon fallback, small contexts
    icon: {
      black: {
        xs: `${CDN}/logos/swim-icon-black-xs.svg`,
        sm: `${CDN}/logos/swim-icon-black-sm.svg`,
        md: `${CDN}/logos/swim-icon-black-md.svg`,
        lg: `${CDN}/logos/swim-icon-black-lg.svg`,
      },
      white: {
        xs: `${CDN}/logos/swim-icon-white-xs.svg`,
        sm: `${CDN}/logos/swim-icon-white-sm.svg`,
        md: `${CDN}/logos/swim-icon-white-md.svg`,
        lg: `${CDN}/logos/swim-icon-white-lg.svg`,
      },
    },

    // ── Stacked (icon above "SWIM" text) ───────────────────────────────────
    // Use in: splash screen, hero sections, anywhere vertical space allows
    stacked: {
      black: {
        sm:   `${CDN}/logos/swim-stacked-black-sm.svg`,
        md:   `${CDN}/logos/swim-stacked-black-md.svg`,
        lg:   `${CDN}/logos/swim-stacked-black-lg.svg`,
        mdPng: `${CDN}/logos/swim-stacked-black-md.png`,
      },
      white: {
        sm:   `${CDN}/logos/swim-stacked-white-sm.svg`,
        md:   `${CDN}/logos/swim-stacked-white-md.svg`,
        lg:   `${CDN}/logos/swim-stacked-white-lg.svg`,
        mdPng: `${CDN}/logos/swim-stacked-white-md.png`,
      },
    },

    // ── Email header logo (transactional emails only — not for web app use) ──
    // Used in: worker/jobs/waitlistDigest and all @swim/email templates
    email: {
      black: `${CDN}/logos/swim-email-black.png`,
    },

    // ── Union (full brand lockup — largest format) ──────────────────────────
    // Use in: marketing pages, print, large hero panels
    union: {
      black: `${CDN}/logos/swim-union-black-xl.svg`,
      white: `${CDN}/logos/swim-union-white-xl.svg`,
    },

    // ── Admin avatar (SWIM superadmin identity — used in dispute threads) ──────
    admin: `${CDN}/logos/swim-admin-avatar.png`,

    // ── Favicon PNGs (CDN reference only — actual favicons served from /public/) ──
    favicon: {
      black: `${CDN}/logos/swim-favicon-black.png`,
      white: `${CDN}/logos/swim-favicon-white.png`,
    },
  },

  // ── Nav icons (custom SWIM water-themed SVGs — left sidebar) ──────────────
  // Location: cdn.swimflows.com/icons/nav/
  // Color applied via CSS — these are single-color black stroke SVGs
  nav: {
    home:      `${CDN}/icons/nav/menu-icon-home-palmtree-32x32.svg`,
    currents:  `${CDN}/icons/nav/menu-icon-currents-32x32.svg`,
    streams:   `${CDN}/icons/nav/menu-icon-streams-32x32.svg`,
    drops:     `${CDN}/icons/nav/menu-icon-drops-32x32.svg`,
    splash:    `${CDN}/icons/nav/menu-icon-splash-32x32.svg`,
    waves:     `${CDN}/icons/nav/menu-icon-waves-32x32.svg`,
    workspace: `${CDN}/icons/nav/menu-icon-workspace-32x32.svg`,
    mbo:       `${CDN}/icons/nav/menu-icon-mbo-32x32.svg`,
  },

  // ── Swimmer icon (standalone SVG — animated in top-left logo + splash) ────
  // Location: cdn.swimflows.com/logos/swimmer-*.svg
  // Note: swimmer-whte-lg.svg has a typo in the CDN filename — matches exactly below
  swimmer: {
    black: {
      sm:    `${CDN}/logos/swimmer-black-sm.svg`,
      smRTL: `${CDN}/logos/swimmer-black-sm-RTL.svg`,
      lg:    `${CDN}/logos/swimmer-black-lg.svg`,
    },
    white: {
      sm:    `${CDN}/logos/swimmer-white-sm.svg`,
      lg:    `${CDN}/logos/swimmer-whte-lg.svg`,  // CDN typo: "whte" — rename when convenient
    },
  },

  // ── Offer images (merchant-uploaded, Phase 6+) ────────────────────────────
  offers: {
    placeholder: `${CDN}/offers/placeholder.png`,  // needs uploading
  },

  // ── Payment provider icons (swim-cdn/icons/payments/) ────────────────────
  // Upload: wrangler r2 object put swim-cdn/icons/payments/stripe.png --file=<file>
  // Upload: wrangler r2 object put swim-cdn/icons/payments/paypal.png --file=<file>
  // Upload: wrangler r2 object put swim-cdn/icons/payments/polar.png --file=<file>
  paymentIcons: {
    stripe: `${CDN}/icons/payments/stripe.png`,
    paypal: `${CDN}/icons/payments/paypal.png`,
    polar:  `${CDN}/icons/payments/polar.png`,
  },

  // ── OG / social preview images ────────────────────────────────────────────
  og: {
    default: `${CDN}/og/swim-og-default.png`,  // needs design + upload
  },

  // ── Marketing / promo videos ──────────────────────────────────────────────
  media: {
    promoV2: `${CDN}/media/swim-promo-2.webm`,
  },
} as const

export type Assets = typeof ASSETS
